const BASE = import.meta?.env?.VITE_API_URL ?? "";

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...opts.headers },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as Record<string,string>;
    throw new Error(err.message ?? err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Bot management ──────────────────────────────────────────

export interface BotListItem {
  id: string;
  botUsername: string;
  status: string;
  businessName: string;
  createdAt: string;
}

export interface BotDetails {
  id: string;
  botUsername: string;
  ownerTelegramId: number;
  status: string;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface BotStats {
  todayAppointments: number;
  totalCustomers: number;
  aiCallsThisMonth: number;
}

export function getBots(ownerTelegramId: number) {
  return request<BotListItem[]>(`/api/bots?ownerTelegramId=${ownerTelegramId}`);
}

export function getBotDetails(botId: string) {
  return request<BotDetails>(`/api/bots/${botId}`);
}

export function getBotStats(botId: string) {
  return request<BotStats>(`/api/bots/${botId}/stats`);
}

export function generateBotConfig(description: string, language = "uz") {
  return request<{
    config: Record<string, unknown>;
    summary: string;
    explanation?: string;
    steps?: Array<{ title: string; description: string; value: string }>;
    model: string;
    cost: number;
  }>("/api/bots/generate", {
    method: "POST",
    body: JSON.stringify({ description, language }),
  });
}

export function createBot(data: {
  config: Record<string, unknown>;
  ownerTelegramId: number;
  botToken: string;
  botUsername: string;
}) {
  return request<{
    botId: string;
    botUsername: string;
    config: Record<string, unknown>;
    createdAt: string;
  }>("/api/bots", { method: "POST", body: JSON.stringify(data) });
}

export function deleteBot(botId: string, ownerTelegramId: number) {
  return request<{ deleted: boolean }>(
    `/api/bots/${botId}?ownerTelegramId=${ownerTelegramId}`,
    { method: "DELETE" },
  );
}

// ── Booking ─────────────────────────────────────────────────

export function getServices(botId: string) {
  return request<Array<{ id: string; name: string; price: number; durationMinutes: number; isActive: boolean }>>(
    `/api/bots/${botId}/services`,
  );
}

export function getStaff(botId: string, serviceId: string) {
  return request<Array<{ id: string; name: string; isActive: boolean }>>(
    `/api/bots/${botId}/staff?serviceId=${serviceId}`,
  );
}

export interface TimeSlot { start: string; end: string }

export function getSlots(botId: string, staffId: string, serviceId: string, date: string) {
  return request<TimeSlot[]>(
    `/api/bots/${botId}/slots?staffId=${staffId}&serviceId=${serviceId}&date=${date}`,
  );
}

export function createAppointment(botId: string, data: {
  customerTelegramId: number;
  serviceId: string;
  staffId: string;
  startTime: string;
  endTime: string;
  customerName: string;
  customerPhone: string;
}) {
  return request<{ appointmentId: string }>(
    `/api/bots/${botId}/appointments`,
    { method: "POST", body: JSON.stringify({ ...data, botId }) },
  );
}

export function getAppointments(botId: string, customerTelegramId: number) {
  return request<Array<{
    id: string; startTime: string; endTime: string;
    serviceId: string; staffId: string; status: string;
    customerName: string; customerPhone: string;
  }>>(`/api/bots/${botId}/appointments?customerTelegramId=${customerTelegramId}`);
}
